import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";

import { supabase } from "@/integrations/supabase/client";
import { AppShell } from "@/components/AppShell";
import { FeatureGate } from "@/components/UpgradeGate";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import {
  DEFAULT_QUARTER_START_MONTH,
  PERIODS,
  periodStart,
  quarterLabel,
  round1,
  weekStart,
  toDateKey,
  type Period,
} from "@/lib/hub";
import { useDomainPlan } from "@/lib/plan";
import { OUTCOME_OPTIONS, outcomeMeta } from "@/lib/people";
import { OutcomeCharts } from "@/components/performance/OutcomeCharts";
import { VarianceCharts } from "@/components/performance/VarianceCharts";

export const Route = createFileRoute("/_authenticated/performance")({
  head: () => ({
    meta: [
      { title: "Progress — MyPriorities" },
      {
        name: "description",
        content:
          "See what you actually completed this week, month, quarter and year: tasks, hours, types, goals and pillars.",
      },
      { property: "og:title", content: "Performance — MyPriorities" },
      {
        property: "og:description",
        content: "Completed work by type, goal and pillar across week, month, quarter and year.",
      },
    ],
  }),
  component: PerformancePage,
});

type Completion = {
  id: string;
  title: string;
  task_type: string;
  goal_id: string | null;
  pillar_id: string | null;
  hours: number;
  expected_hours: number | null;
  actual_hours: number | null;
  travel_hours: number;
  recurrence: string;
  completed_on: string;
  outcome_status: string;
  result_notes: string | null;
  completion_people: { person_id: string; people: { name: string } | null }[];
  work_type: string | null;
  product_type: string | null;
  market: string | null;
  category: string | null;
  territory: string | null;
  client: string | null;
};

import { parseTagValues } from "@/lib/taxonomy";
import { useClassification } from "@/lib/classification";

function PerformancePage() {
  const [period, setPeriod] = useState<Period>("week");
  const plan = useDomainPlan("professional");
  const { enabledFields: classificationFields } = useClassification();

  const settingsQuery = useQuery({
    queryKey: ["settings"],
    queryFn: async () => {
      const { data, error } = await supabase.from("settings").select("*").maybeSingle();
      if (error) throw error;
      return data as { quarter_start_month: number } | null;
    },
  });
  const quarterStartMonth = Number(
    settingsQuery.data?.quarter_start_month ?? DEFAULT_QUARTER_START_MONTH,
  );

  const from = periodStart(period, new Date(), quarterStartMonth);

  const completionsQuery = useQuery({
    queryKey: ["task_completions", period, from],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("task_completions")
        .select(
          "id, title, task_type, goal_id, pillar_id, hours, travel_hours, recurrence, completed_on, outcome_status, result_notes, work_type, product_type, market, category, territory, client, completion_people(person_id, people(name))",
        )
        .gte("completed_on", from)
        .order("completed_on", { ascending: false });
      if (error) throw error;
      return (data ?? []) as Completion[];
    },
  });

  const openQuery = useQuery({
    queryKey: ["tasks"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("tasks")
        .select("id, done, recurrence, last_completed_on");
      if (error) throw error;
      return data as { id: string; done: boolean }[];
    },
  });

  const rows = completionsQuery.data ?? [];
  const openCount = (openQuery.data ?? []).filter((t) => !t.done).length;

  const stats = useMemo(() => {
    const hours = rows.reduce((s, r) => s + Number(r.hours || 0), 0);
    const travel = rows.reduce((s, r) => s + Number(r.travel_hours || 0), 0);
    const recurring = rows.filter((r) => r.recurrence !== "none").length;
    const rate = rows.length + openCount ? (rows.length / (rows.length + openCount)) * 100 : 0;
    return { count: rows.length, hours, travel, recurring, rate };
  }, [rows, openCount]);

  const byType = useMemo(() => {
    const map = new Map<string, { count: number; hours: number }>();
    for (const r of rows) {
      const cur = map.get(r.task_type) ?? { count: 0, hours: 0 };
      map.set(r.task_type, { count: cur.count + 1, hours: cur.hours + Number(r.hours || 0) });
    }
    return [...map.entries()]
      .map(([name, v]) => ({ name, ...v }))
      .sort((a, b) => b.count - a.count);
  }, [rows]);

  const byTag = useMemo(
    () =>
      classificationFields
        .map((field) => {
          const map = new Map<string, { count: number; hours: number }>();
          for (const r of rows) {
            const values = parseTagValues(r[field.key]);
            if (!values.length) continue;
            const share = Number(r.hours || 0) / values.length;
            for (const value of values) {
              const cur = map.get(value) ?? { count: 0, hours: 0 };
              map.set(value, { count: cur.count + 1, hours: cur.hours + share });
            }
          }
          return {
            key: field.key,
            label: field.label,
            rows: [...map.entries()]
              .map(([name, v]) => ({ name, ...v }))
              .sort((a, b) => b.count - a.count),
          };
        })
        .filter((f) => f.rows.length > 0),
    [rows, classificationFields],
  );

  const byOutcome = useMemo(
    () =>
      OUTCOME_OPTIONS.map((o) => ({
        ...o,
        count: rows.filter((r) => r.outcome_status === o.value).length,
      })).filter((o) => o.count > 0),
    [rows],
  );

  const recorded = rows.filter((r) => r.outcome_status !== "unrecorded").length;

  const byPerson = useMemo(() => {
    const map = new Map<string, number>();
    for (const r of rows) {
      for (const link of r.completion_people ?? []) {
        const name = link.people?.name;
        if (!name) continue;
        map.set(name, (map.get(name) ?? 0) + 1);
      }
    }
    return [...map.entries()]
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);
  }, [rows]);

  const byPillar = useMemo(() => {
    const out = plan.allPillars.map((p) => {
      const owned = rows.filter((r) => r.pillar_id === p.id);
      return {
        id: p.id,
        name: p.name,
        count: owned.length,
        hours: owned.reduce((s, r) => s + Number(r.hours || 0), 0),
        goals: plan.allGoals
          .filter((g) => g.pillar_id === p.id)
          .map((g) => {
            const gr = owned.filter((r) => r.goal_id === g.id);
            return {
              id: g.id,
              name: g.name,
              count: gr.length,
              hours: gr.reduce((s, r) => s + Number(r.hours || 0), 0),
            };
          })
          .filter((g) => g.count > 0),
      };
    });
    const loose = rows.filter((r) => !r.pillar_id);
    if (loose.length) {
      out.push({
        id: "unassigned",
        name: "Unassigned",
        count: loose.length,
        hours: loose.reduce((s, r) => s + Number(r.hours || 0), 0),
        goals: [],
      });
    }
    return out.filter((p) => p.count > 0);
  }, [rows, plan.allPillars, plan.allGoals]);

  const trend = useMemo(() => {
    const buckets = new Map<string, number>();
    for (const r of rows) {
      const key = weekStart(new Date(`${r.completed_on}T00:00:00`));
      buckets.set(key, (buckets.get(key) ?? 0) + 1);
    }
    const weeks: { key: string; count: number }[] = [];
    let cursor = new Date(`${weekStart(new Date(`${from}T00:00:00`))}T00:00:00`);
    const end = new Date();
    while (cursor <= end && weeks.length < 60) {
      const key = toDateKey(cursor);
      weeks.push({ key, count: buckets.get(key) ?? 0 });
      cursor = new Date(cursor);
      cursor.setDate(cursor.getDate() + 7);
    }
    return weeks;
  }, [rows, from]);

  const maxTrend = Math.max(1, ...trend.map((t) => t.count));

  return (
    <AppShell title="Progress" subtitle="What you actually finished, not what you planned.">
      <div className="flex flex-wrap gap-2">
        {PERIODS.map((p) => (
          <button
            key={p.value}
            onClick={() => setPeriod(p.value)}
            className={`rounded-full border px-3 py-1 text-xs transition-colors ${
              period === p.value
                ? "border-primary bg-primary/10 text-foreground"
                : "border-border text-muted-foreground hover:bg-accent"
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>
      <p className="mt-2 text-xs text-muted-foreground">
        Quarters run {quarterStartMonth === 1 ? "on the calendar" : "on your own start month"} —
        you're in {quarterLabel(new Date(), quarterStartMonth)}.
      </p>

      <section className="mt-6 grid fill-track gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="Tasks completed" value={String(stats.count)} />
        <Stat
          label="Hours spent"
          value={`${round1(stats.hours)}h`}
          hint={stats.travel > 0 ? `${round1(stats.travel)}h travel` : undefined}
        />

        <Stat label="Completion rate" value={`${Math.round(stats.rate)}%`} />
        <Stat label="Recurring completions" value={String(stats.recurring)} />
      </section>

      {rows.length === 0 ? (
        <p className="panel mt-6 p-6 text-sm text-muted-foreground">
          Nothing completed in this period yet. Tick tasks off in the Work hub and they'll show up
          here.
        </p>
      ) : (
        <>
          {byTag.length ? (
            <section className="mt-10 grid fill-track gap-6 lg:grid-cols-2 xl:grid-cols-3">
              {byTag.map((field) => (
                <div key={field.key} className="panel p-6">
                  <h2 className="label-caps">By {field.label.toLowerCase()}</h2>
                  <div className="mt-4 space-y-3">
                    {field.rows.map((row) => (
                      <div key={row.name}>
                        <div className="flex items-baseline justify-between text-sm">
                          <span className="font-medium">{row.name}</span>
                          <span className="text-xs text-muted-foreground">
                            {row.count} · {round1(row.hours)}h
                          </span>
                        </div>
                        <Progress
                          value={(row.count / Math.max(1, stats.count)) * 100}
                          className="mt-1.5"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </section>
          ) : null}

          <section className="mt-10 grid fill-track gap-6 lg:grid-cols-2">
            <div className="panel p-6">
              <h2 className="label-caps">By type of work</h2>
              <div className="mt-4 space-y-3">
                {byType.map((t) => (
                  <div key={t.name}>
                    <div className="flex items-baseline justify-between text-sm">
                      <span className="font-medium">{t.name}</span>
                      <span className="text-xs text-muted-foreground">
                        {t.count} · {round1(t.hours)}h
                      </span>
                    </div>
                    <Progress
                      value={(t.count / Math.max(1, stats.count)) * 100}
                      className="mt-1.5"
                    />
                  </div>
                ))}
              </div>
            </div>

            <div className="panel p-6">
              <h2 className="label-caps">Towards which pillar</h2>
              <div className="mt-4 space-y-4">
                {byPillar.map((p) => (
                  <div key={p.id}>
                    <div className="flex items-baseline justify-between text-sm">
                      <span className="font-medium">{p.name}</span>
                      <span className="text-xs text-muted-foreground">
                        {p.count} · {round1(p.hours)}h
                      </span>
                    </div>
                    <Progress
                      value={(p.count / Math.max(1, stats.count)) * 100}
                      className="mt-1.5"
                    />
                    {p.goals.length ? (
                      <ul className="mt-1.5 space-y-0.5 pl-3 text-xs text-muted-foreground">
                        {p.goals.map((g) => (
                          <li key={g.id}>
                            ↳ {g.name} — {g.count} task{g.count > 1 ? "s" : ""}, {round1(g.hours)}h
                          </li>
                        ))}
                      </ul>
                    ) : null}
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="mt-6 grid fill-track gap-6 lg:grid-cols-2">
            <div className="panel p-6">
              <h2 className="label-caps">Outcomes</h2>
              {recorded === 0 ? (
                <p className="mt-3 text-sm text-muted-foreground">
                  No results recorded yet — the next time you tick a task off, say how it went.
                </p>
              ) : (
                <div className="mt-4 space-y-3">
                  {byOutcome.map((o) => (
                    <div key={o.value}>
                      <div className="flex items-baseline justify-between text-sm">
                        <Badge variant="outline" className={o.className}>
                          {o.label}
                        </Badge>
                        <span className="text-xs text-muted-foreground">{o.count}</span>
                      </div>
                      <Progress
                        value={(o.count / Math.max(1, recorded)) * 100}
                        className="mt-1.5"
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="panel p-6">
              <h2 className="label-caps">Worked with</h2>
              {byPerson.length === 0 ? (
                <p className="mt-3 text-sm text-muted-foreground">
                  Nobody logged against this period's completions yet.
                </p>
              ) : (
                <div className="mt-4 space-y-3">
                  {byPerson.map((p) => (
                    <div key={p.name}>
                      <div className="flex items-baseline justify-between text-sm">
                        <span className="font-medium">{p.name}</span>
                        <span className="text-xs text-muted-foreground">
                          {p.count} task{p.count > 1 ? "s" : ""}
                        </span>
                      </div>
                      <Progress
                        value={(p.count / Math.max(1, stats.count)) * 100}
                        className="mt-1.5"
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>

          <OutcomeCharts rows={rows} from={from} />

          <div className="mt-6">
            <VarianceCharts rows={rows} />
          </div>

          <section className="panel mt-6 p-6">
            <h2 className="label-caps">Completions per week</h2>
            <div className="mt-4 flex h-32 items-end gap-1.5">
              {trend.map((w) => (
                <div key={w.key} className="flex flex-1 flex-col items-center gap-1">
                  <div
                    className="w-full rounded-t bg-primary/70"
                    style={{ height: `${(w.count / maxTrend) * 100}%`, minHeight: 2 }}
                    title={`Week of ${w.key}: ${w.count}`}
                  />
                  <span className="text-[10px] text-muted-foreground">{w.key.slice(8)}</span>
                </div>
              ))}
            </div>
          </section>

          <section className="mt-10">
            <h2 className="label-caps">Completed in this period</h2>
            <div className="panel mt-3 divide-y divide-border">
              {rows.map((r) => {
                const outcome = outcomeMeta(r.outcome_status);
                const names = (r.completion_people ?? [])
                  .map((l) => l.people?.name)
                  .filter(Boolean) as string[];
                return (
                  <div key={r.id} className="p-4 text-sm">
                    <div className="flex flex-wrap items-center gap-3">
                      <span className="font-medium">{r.title}</span>
                      <Badge variant="secondary">{r.task_type}</Badge>
                      {r.recurrence !== "none" ? <Badge variant="outline">Recurring</Badge> : null}
                      {outcome ? (
                        <Badge variant="outline" className={outcome.className}>
                          {outcome.label}
                        </Badge>
                      ) : null}
                      <span className="ml-auto text-xs text-muted-foreground">
                        {r.completed_on} · {round1(Number(r.hours))}h
                      </span>
                    </div>
                    {r.result_notes ? (
                      <p className="mt-1.5 text-xs text-muted-foreground">{r.result_notes}</p>
                    ) : null}
                    {names.length ? (
                      <p className="mt-1 text-xs text-muted-foreground">With {names.join(", ")}</p>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </section>
        </>
      )}
    </AppShell>
  );
}

function Stat({ label, value, hint }: { label: string; value: string; hint?: string | undefined }) {
  return (
    <div className="panel p-5">
      <p className="label-caps">{label}</p>
      <p className="mt-2 font-display text-3xl font-bold text-primary">{value}</p>
      {hint ? <p className="mt-1 text-xs text-muted-foreground">{hint}</p> : null}
    </div>
  );
}
